import { type PayloadAction, createReducer } from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import {
    duplicatesLoadMoreRequest,
    duplicatesLoadMoreSuccess,
    loadDuplicatesCountFail,
    loadDuplicatesCountSuccess,
    loadDuplicatesListFail,
    loadDuplicatesListSuccess,
    loadDuplicatesPreviewFail,
    loadDuplicatesPreviewSuccess,
    resetDuplicatesList,
    setDuplicatesDeletionComplete,
    showBestPhotoTooltip,
} from 'reactApp/modules/duplicates/duplicates.actions';
import type {
    IDuplicateItem,
    IDuplicateItemResponse,
    IDuplicatesGroupResponse,
    IDuplicatesLoadMoreSuccess,
    IDuplicatesState,
    ILoadDuplicatesCountSuccess,
    ILoadDuplicatesListSuccess,
    ILoadDuplicatesPreviewSuccess,
} from 'reactApp/modules/duplicates/duplicates.types';
import { getExtension } from 'reactApp/modules/file/helpers/getExtention';
import { getItemNameWithoutExt } from 'reactApp/modules/file/utils';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import { normalizeMtime } from 'reactApp/utils/tree.helpers';

import { DUPLICATES_PAGE_BASE_URL, REMOVE_ALL_DUPLICATES_TOOLTIP_ID } from './duplicates.constants';

const fileSchema = new schema.Entity(
    'files',
    {},
    {
        idAttribute: 'node_id',
        processStrategy: (value, parent) => {
            return normalizeFileItem(value, parent);
        },
    }
);

const normalizeFileItem = (file: IDuplicateItemResponse, group: IDuplicatesGroupResponse): IDuplicateItem => {
    const id = file.node_id;
    const path = file.path || '';
    const size = file.size;
    const ext = getExtension(file.name);
    const kind = 'image';
    const subKind = 'picture';
    const name = file.name;
    const urlBuilderParams = path ? { ext, id: path, size, path, kind, subKind, name } : undefined;

    return {
        mtime: normalizeMtime(file.mtime),
        size,
        path,
        name,
        id,
        storage: EStorageType.duplicates,
        isFolder: false,
        ext,
        nameWithoutExt: getItemNameWithoutExt(name, ext),
        parent: DUPLICATES_PAGE_BASE_URL,
        thumbnails: urlBuilderParams ? urlBuilder.getThumb(urlBuilderParams) : undefined,
        url: urlBuilderParams ? urlBuilder.getUrls(urlBuilderParams) : undefined,
        type: 'image',
        isReadOnly: false,
        isInFavorites: file.favorited,
        kind: 'image',
        subKind: 'picture',
        uflr: undefined,
        groupId: group.id,
        home: name,
        best: group.best_photo === file.node_id,
        position: group.files.findIndex((photo) => photo.node_id === file.node_id) + 1,
        groupLength: group.files.length,
    };
};

const groupSchema = new schema.Entity(
    'groups',
    {
        files: [fileSchema],
    },
    {
        idAttribute: 'id',
    }
);

const initialState: IDuplicatesState = {
    preview: {
        list: [],
        isLoaded: false,
        isLoading: false,
        isError: false,
    },
    items: {
        id: 'duplicates',
        list: {},
        listIds: [],
        childs: [],
        isLoaded: false,
        isLoading: true,
        error: false,
        hasMoreToLoad: false,
    },
    count: {
        count: 0,
        total: 0,
        isLoaded: false,
        isLoading: true,
        isError: false,
    },
    groups: {
        list: {},
    },
    showBestPhotoTooltip: Boolean(storeHelper.getValue(REMOVE_ALL_DUPLICATES_TOOLTIP_ID)),
    deletionComplete: false,
};

const urlBuilder = new UrlBuilder();

export const duplicatesReducer = createReducer(initialState, {
    [loadDuplicatesListSuccess.type]: (state, action: PayloadAction<ILoadDuplicatesListSuccess>) => {
        const { list, cursor, count, total } = action.payload;

        const normalized = normalize(list, [groupSchema]);
        const listIds = normalized.result.flatMap((groupId: string) => {
            const group = normalized.entities.groups?.[groupId];
            return group?.files || [];
        });

        state.items = {
            id: 'duplicates',
            cursor,
            list: normalized.entities.files || {},
            listIds,
            childs: listIds,
            isLoaded: true,
            isLoading: false,
            error: false,
            hasMoreToLoad: !!cursor,
        };
        state.groups.list = normalized.entities.groups || {};
        state.count = {
            count,
            total,
            isLoaded: true,
            isLoading: false,
            isError: false,
        };
    },
    [duplicatesLoadMoreRequest.type]: (state) => {
        state.items.isLoading = true;
        state.items.error = false;
    },
    [duplicatesLoadMoreSuccess.type]: (state, action: PayloadAction<IDuplicatesLoadMoreSuccess>) => {
        const { list, cursor } = action.payload;

        const normalized = normalize(list, [groupSchema]);
        const newListIds = normalized.result.flatMap((groupId: string) => {
            const group = normalized.entities.groups?.[groupId];
            return group?.files || [];
        });

        state.items = {
            ...state.items,
            cursor,
            list: {
                ...state.items.list,
                ...(normalized.entities.files || {}),
            },
            listIds: [...state.items.listIds, ...newListIds],
            childs: [...state.items.childs, ...newListIds],
            isLoaded: true,
            isLoading: false,
            error: false,
            hasMoreToLoad: !!cursor,
        };

        state.groups.list = {
            ...state.groups.list,
            ...(normalized.entities.groups || {}),
        };
    },
    [loadDuplicatesListFail.type]: (state) => {
        state.items.isLoading = false;
        state.items.isLoaded = true;
        state.items.error = true;
    },
    [resetDuplicatesList.type]: (state) => {
        state.items = {
            id: 'duplicates',
            cursor: undefined,
            list: {},
            listIds: [],
            childs: [],
            isLoaded: false,
            isLoading: false,
            error: false,
            hasMoreToLoad: false,
        };
    },
    [loadDuplicatesPreviewSuccess.type]: (state, action: PayloadAction<ILoadDuplicatesPreviewSuccess>) => {
        const { list } = action.payload;

        state.preview = {
            list,
            isLoaded: true,
            isLoading: false,
            isError: false,
        };
    },
    [loadDuplicatesPreviewFail.type]: (state) => {
        state.preview.isLoading = false;
        state.preview.isLoaded = true;
        state.preview.isError = true;
    },
    [loadDuplicatesCountSuccess.type]: (state, action: PayloadAction<ILoadDuplicatesCountSuccess>) => {
        const { count, total } = action.payload.duplicates;

        state.count = {
            count,
            total,
            isLoaded: true,
            isLoading: false,
            isError: false,
        };

        if (state.deletionComplete) {
            state.deletionComplete = count === 0;
        }
    },
    [loadDuplicatesCountFail.type]: (state) => {
        state.count.isLoading = false;
        state.count.isLoaded = true;
        state.count.isError = true;
    },
    [showBestPhotoTooltip.type]: (state) => {
        state.showBestPhotoTooltip = true;
    },
    [setDuplicatesDeletionComplete.type]: (state) => {
        state.deletionComplete = true;
    },
});
